import { useCallback, useEffect, useState } from 'react';
import { TrackedEvents } from '../../features/tracking';
import { useTrack } from '../useTrack/useTrack';
import { singletonHook } from 'react-singleton-hook';
import { getLogger } from '../../libs/logger';

const logger = getLogger('HighCpuTracking');

const hookInitValues = {
  addHighCpuReason: () => undefined,
  removeHighCpuReason: () => undefined,
};

type PossibleReasons = 'virtual background';

/**
 * Track high CPU usage and possible reasons for it.
 */
export function useTrackHighCpuImpl() {
  const [reasons, setReasons] = useState(new Set());
  const track = useTrack();

  const addHighCpuReason = useCallback((reason: PossibleReasons) => {
    setReasons((prev) => {
      return new Set(prev?.add(reason));
    });
  }, []);

  const removeHighCpuReason = useCallback((reason: PossibleReasons) => {
    setReasons((prev) => {
      prev?.delete(reason);
      return new Set(prev);
    });
  }, []);

  useEffect(() => {
    // Check if PressureObserver API is available in the environment
    if (typeof PressureObserver === 'undefined') {
      return;
    }

    let count = 0;

    // Create a new PressureObserver
    const observer = new PressureObserver((changes) => {
      const state = changes[changes.length - 1]?.state;

      // Increment count if CPU is under stress
      if (state === 'critical' || state === 'serious') {
        count++;
      } else {
        count = 0;
      }

      // Track event only after 5 consecutive high-pressure readings
      if (count === 5) {
        const currentReasons = Array.from(reasons ?? []);

        // Send tracking event
        void track(TrackedEvents.HighCpuDetected, {
          reasons: currentReasons,
        });
        logger.info(TrackedEvents.HighCpuDetected, {
          reasons: currentReasons,
        });

        disconnectObserver();
      }
    });

    // check CPU pressure every 4 seconds
    const cpuPressureObserver = observer.observe('cpu', {
      sampleInterval: 4_000,
    });

    const disconnectObserver = () => {
      cpuPressureObserver
        .then(() => observer.disconnect())
        .catch(() => {
          /* ignore if already disconnected */
        });
    };

    return disconnectObserver;
  }, [reasons?.size]);

  return {
    addHighCpuReason,
    removeHighCpuReason,
  };
}

export const useTrackHighCpu = singletonHook(
  hookInitValues,
  useTrackHighCpuImpl,
  { unmountIfNoConsumers: true },
);
